import { ValueRanges } from 'rlv-common';
import { number as yupNumber, object, ObjectSchema, ref, Schema } from 'yup';
import { EURO_WITHOUT_CENT, formatCurrency } from '../helpers/formattingHelper';
import { getCommonValidationParameters } from './CommonValidations';
require('./validation.ts'); // required for CustomMixedNumberSchema to work

export function decreasingSumInsuredValidation(valueRanges: ValueRanges): Schema<any> {
  return createDecreasingNeedSchema(valueRanges)
    .concat(sumInsuredValidator(valueRanges))
    .concat(insuranceEndSumValidator(valueRanges))
    .concat(yearsTermValidator(valueRanges));
}

const validateIncrementBy = (value: number, increment: number | undefined) => {
  if (increment) {
    return value % increment === 0;
  }
  return true;
};

export const createDecreasingNeedSchema = (valueRanges: ValueRanges): Schema<any> => {
  const STEP_RANGE_SUM_INSURED = 1000;
  const insuranceEndSum = valueRanges.insuranceEndSum;
  const currencyFormatter = formatCurrency(EURO_WITHOUT_CENT);
  const insuranceEndSumMinMaxError = `Die Versicherungssumme zu Beginn des letzten Jahres muss mindestens
  ${currencyFormatter.format(
    insuranceEndSum.min,
  )} und darf maximal die Versicherungssumme zu Beginn des ersten Jahres sein.`;

  return getCommonValidationParameters(valueRanges).concat(
    object().shape({
      insuranceEndSum: yupNumber()
        .validateNumber()
        .required('Bitte geben Sie die Versicherungssumme zum Ende des letzten Vertragsjahres ein.')
        .isSmallerThan(
          ref('sumInsured'),
          'Bitte beachten Sie, dass die Summe zum Versicherungsende kleiner sein muss, als die Summe zum Versicherungsbeginn.',
          STEP_RANGE_SUM_INSURED,
        )
        .min(insuranceEndSum.min, insuranceEndSumMinMaxError)
        .test(
          'isFullThousandEuro',
          'Es können nur ganze Tausend Eurobeträge als Versicherungssumme zum Ende des letzten Vertragsjahres eingegeben werden.',
          (value: number) => value % STEP_RANGE_SUM_INSURED === 0,
        ),
    }),
  );
};

export const sumInsuredValidator = (valueRanges: ValueRanges): ObjectSchema => {
  const {
    insuranceSum: { increment, max, min },
  } = valueRanges;

  // TODO: Should be checked because there is a conflict between the User Story and the values that come from the server
  const OUT_OF_RANGE_MSG = `Bitte wählen Sie eine Summe zwischen ${min.toLocaleString(
    'de-DE',
  )} und ${max.toLocaleString('de-DE')} € (in ${
    increment ? increment.toLocaleString('de-DE') : ''
  }er-Schritten).`;

  return object().shape({
    sumInsured: yupNumber()
      .required('Darf nicht leer sein')
      .min(min, OUT_OF_RANGE_MSG)
      .max(max, OUT_OF_RANGE_MSG)
      .test('Multiple', OUT_OF_RANGE_MSG, value => validateIncrementBy(value, increment)),
  });
};

export const yearsTermValidator = (valueRanges: ValueRanges) => {
  const {
    insuranceDuration: { max, min },
  } = valueRanges;

  const OUT_OF_RANGE_MSG = `Bitte wählen Sie eine Laufzeit zwischen ${min} und ${max} Jahren.`;

  return object().shape({
    periodInYears: yupNumber()
      .required('Darf nicht leer sein.')
      .min(min, OUT_OF_RANGE_MSG)
      .max(max, OUT_OF_RANGE_MSG),
  });
};

export const insuranceEndSumValidator = (valueRanges: ValueRanges) => {
  const {
    insuranceEndSum: { increment, max, min },
  } = valueRanges;

  const OUT_OF_RANGE_MSG = `Bitte wählen Sie eine Summe zwischen ${min.toLocaleString(
    'de-DE',
  )} und ${max.toLocaleString('de-DE')} € (in ${
    increment ? increment.toLocaleString('de-DE') : ''
  }er-Schritten).`;

  return object().shape({
    insuranceEndSum: yupNumber()
      .required('Darf nicht leer sein.')
      .min(min, OUT_OF_RANGE_MSG)
      .max(max, OUT_OF_RANGE_MSG)
      .test('Multiple', OUT_OF_RANGE_MSG, value => validateIncrementBy(value, increment)),
  });
};
